import React, { useEffect, useState } from 'react'
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    FormControl,
    IconButton,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
    Grid
} from "@mui/material"

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined"
import AddIcon from "@mui/icons-material/Add"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import SectionHeader from "./SectionHeader"
import DeleteIcon from "@mui/icons-material/Delete";

const GRID_COLS = "56px 140px 120px minmax(0, 1fr) 56px"

function FormItems({
    itemsRemito,
    um,
    productos,
    handleOnChangeItemsRemito,
    handleOnChangeUM,
    handleOnChangeProducto,
    handleNuevoItem,
    handleEliminarItem,
    lineasCompletas,
    setLineasCompletas
}) {
    const theme = useTheme()
    const isCompact = useMediaQuery(theme.breakpoints.down("md"))

    const setProductosOptionsDisabled = (producto, index) => {
        const porUm = !(producto?.um.split(",").map(um => Number(um)).includes(itemsRemito[index].unidadMedida))
        const productRepetido = itemsRemito.some(item=> item.idProducto === producto.idproducto) 

        return porUm || productRepetido;
    }
    const totalUnidades = itemsRemito?.reduce((acc, it) => acc + (Number(it.cantidad) || 0), 0)

    useEffect(()=>{
        itemsRemito && setLineasCompletas(itemsRemito?.filter((it) => it.cantidad > 0 && it.unidadMedida && it.idProducto)?.length)
    },[itemsRemito])

    return (
        <Paper
            elevation={0}
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                overflow: "hidden",
            }}
        >
            <SectionHeader
                icon={<Inventory2OutlinedIcon fontSize="small" />}
                title="Renglones del remito"
                subtitle="Productos y servicios que integran el comprobante"
            />
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                {!isCompact && (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: GRID_COLS,
                            alignItems: "center",
                            gap: 2,
                            px: 2,
                            pb: 1,
                        }}
                    >
                        <HeaderCell label="#" />
                        <HeaderCell label="Cantidad" />
                        <HeaderCell label="U. Medida" />
                        <HeaderCell label="Producto / Servicio" />
                        <HeaderCell label="" />
                    </Box>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {itemsRemito?.map((field, index) => (
                        <>
                            <Box
                                key={index}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: isCompact ? "1fr" : GRID_COLS,
                                    alignItems: "center",
                                    gap: 2,
                                    p: 2,
                                    borderRadius: 2.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.015),
                                    transition: "border-color .2s, box-shadow .2s",
                                    "&:hover": {
                                        borderColor: (t) => alpha(t.palette.primary.main, 0.35),
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: isCompact ? "none" : "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 32,
                                        height: 32,
                                        borderRadius: "50%",
                                        fontWeight: 700,
                                        fontSize: 13,
                                        color: "primary.main",
                                        bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                                    }}
                                >
                                    {index + 1}
                                </Box>

                                <Field label={isCompact ? "Cantidad" : null}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        name="cantidad"
                                        type="number"
                                        placeholder="0"
                                        value={field.cantidad}
                                        onChange={(e) => handleOnChangeItemsRemito(e, index)}
                                        inputProps={{ min: 0, style: { textAlign: "right", fontVariantNumeric: "tabular-nums" } }}
                                    />
                                </Field>

                                <Field label={isCompact ? "U. Medida" : null}>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            name="unidadMedida"
                                            displayEmpty
                                            value={field.unidadMedida}
                                            onChange={(e) => handleOnChangeUM(e, index)}
                                            renderValue={(val) => {
                                                if (!val) return <Typography color="text.disabled" variant="body2">UM</Typography>
                                                return um?.find((u) => u.id === val)?.abreviatura?.toUpperCase()
                                            }}
                                        >
                                            {um?.map((unidad) => (
                                                <MenuItem key={unidad.id} value={unidad.id} 
                                                >
                                                    {unidad?.abreviatura?.toUpperCase()}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Field>
                                <Field label={isCompact ? "Producto / Servicio" : null}>
                                    <Autocomplete
                                        size="small"
                                        fullWidth
                                        disableClearable
                                        options={productos || []}
                                        getOptionDisabled={(producto) => setProductosOptionsDisabled(producto, index)}
                                        value={productos?.find((p) => p.idproducto === field.idProducto) || null}
                                        onChange={(e, value) => handleOnChangeProducto(value, index)}
                                        noOptionsText="No se encontraron coincidencias..."
                                        getOptionLabel={(option) => option?.detalle_factura || ""}
                                        isOptionEqualToValue={(option, value) => option?.idproducto === value?.idproducto}
                                        renderOption={(props, option) => {
                                            const { key, ...rest } = props
                                            return (
                                                <Box component="li" key={option.idproducto} {...rest}>
                                                    {option.detalle_factura}
                                                </Box>
                                            )
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={field.unidadMedida ? "Buscar producto..." : "Elegí primero la unidad de medida"}
                                            />
                                        )}
                                    />
                                </Field>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: isCompact ? "flex-end" : "center",
                                    }}
                                >
                                    <Tooltip title="Eliminar renglón">
                                        <span>
                                            <IconButton
                                                disabled={itemsRemito?.length === 1}
                                                onClick={() => handleEliminarItem(index)}
                                                color="error"
                                                size="small"
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </>
                    ))}
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleNuevoItem}
                        sx={{
                            alignSelf: "flex-start",
                            mt: 1,
                            borderRadius: 2,
                            textTransform: "none"
                        }}
                    >
                        Añadir renglón
                    </Button>
                    <Box
                        sx={{
                            mt: 2.5,
                            pt: 2,
                            borderTop: "1px dashed",
                            borderColor: "divider",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1.5,
                            alignItems: "center",
                        }}
                    >
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`${itemsRemito?.length} renglón${itemsRemito?.length === 1 ? "" : "es"}`}
                        />
                        <Chip size="small" color={lineasCompletas ? "success": "warning"} variant="outlined" label={`${lineasCompletas} completo${lineasCompletas === 1 ? "" : "s"}`} />
                        <Box sx={{ flex: 1 }} />
                    </Box>
                </Box>
            </Box>
        </Paper>
    )
}

function HeaderCell({ label }) {
    return (
        <Typography variant="overline" color="text.secondary" sx={{ fontSize: 11 }}>
            {label}
        </Typography>
    )
}

function Field({ label, children }) {
    if (!label) return children
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>
                {label}
            </Typography>
            {children}
        </Box>
    )
}

export default FormItems
